import React, { useState } from 'react';
import { Check, ChevronDown, ChevronUp, Star, BookOpen, Users, Clock, Zap, ShieldCheck } from 'lucide-react';
const CHECKOUT_URL = 'https://pay.hotmart.com/H104223457N?checkoutMode=10';
const IMAGES = {
  main: 'https://cdn.magicpatterns.com/uploads/mrEAD6N9YUn1PV84RABLbx/1_Principal_.png',
  bono1: 'https://cdn.magicpatterns.com/uploads/4erFD1x23WyW3KUJzibmRC/bono_1.png',
  bono2: 'https://cdn.magicpatterns.com/uploads/7JbhrRKTcszhunuULNUupf/Bono_2.png',
  bono3: 'https://cdn.magicpatterns.com/uploads/uoPCACAkD1hW6rVsprEiaM/bono_3.png',
  bono4: 'https://cdn.magicpatterns.com/uploads/vtFcxRpcq1NztSdENErkgf/bono_4.png'
};
const TESTIMONIALS = [{
  name: 'María González',
  role: 'Docente',
  location: 'Buenos Aires, Argentina',
  text: 'Antes pasaba horas buscando noticias y tratando de adaptarlas. Con Economía Lista, tengo todo armado. Mis alumnos por fin entienden por qué suben los precios.',
  age: 42
}, {
  name: 'Carlos Mendoza',
  role: 'Profesor de Ciencias Sociales',
  location: 'Ciudad de México, México',
  text: 'La simulación de mercado cambió la dinámica de mi clase. Pasaron de estar aburridos a debatir sobre oferta y demanda con pasión. ¡Increíble!',
  age: 38
}, {
  name: 'Ana Lucía Vargas',
  role: 'Maestra',
  location: 'Bogotá, Colombia',
  text: 'Lo que más me gusta son los ejemplos cotidianos. Explicar las tarjetas de crédito con casos reales hizo que mis estudiantes prestaran atención real.',
  age: 45
}, {
  name: 'Roberto Silva',
  role: 'Docente',
  location: 'Lima, Perú',
  text: 'Material de primera calidad. Las rúbricas y evaluaciones me ahorraron fines de semana enteros de trabajo. Vale cada centavo.',
  age: 35
}, {
  name: 'Patricia Morales',
  role: 'Profesora de Economía',
  location: 'Santiago, Chile',
  text: 'Es un sistema completo, no solo actividades sueltas. Mis clases tienen una estructura clara y los chicos participan muchísimo más.',
  age: 48
}];
const FAQS = [{
  question: '¿Este material sirve si no soy especialista en economía?',
  answer: 'Sí. Está diseñado con explicaciones simples, ejemplos cotidianos y actividades guiadas para que cualquier docente pueda usarlo sin ser economista.'
}, {
  question: '¿Para qué edades o niveles funciona?',
  answer: 'Se adapta a cualquier nivel educativo donde se trabajen temas económicos básicos, ciudadanía o ciencias sociales.'
}, {
  question: '¿Es solo teoría o tiene actividades?',
  answer: 'Es 100% práctico. Incluye simulaciones, casos reales, debates, actividades grupales y evaluaciones listas.'
}, {
  question: '¿Cuánto tiempo me ahorra?',
  answer: 'Mucho. Ya no necesitas inventar ejemplos ni actividades desde cero. Solo descargar, imprimir y usar.'
}, {
  question: '¿Puedo imprimir el material?',
  answer: 'Sí. Está preparado para uso digital o impresión.'
}, {
  question: '¿Cómo recibo el producto?',
  answer: 'Acceso inmediato tras la compra. Descarga digital directa.'
}, {
  question: '¿Puedo usarlo todo el año?',
  answer: 'Sí. Está pensado como sistema completo, no como material aislado.'
}];
function Section({
  className,
  children,
  id
}: {
  className?: string;
  children: React.ReactNode;
  id?: string;
}) {
  return <section id={id} className={`py-16 px-4 md:px-8 ${className}`} data-id="element-7">
      {children}
    </section>;
}
// Botón que redirige al checkout (solo para sección de compra)
function CheckoutButton({
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button onClick={() => window.location.href = CHECKOUT_URL} className={`bg-[#c9a227] hover:bg-[#b08d1f] text-white font-bold py-4 px-8 rounded-lg shadow-lg transform transition hover:scale-105 text-lg md:text-xl w-full md:w-auto uppercase tracking-wide ${className}`} {...props} data-id="element-8">
      {children}
    </button>;
}
// Botón que hace scroll a la sección de compra
function ScrollButton({
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button onClick={() => document.getElementById('comprar')?.scrollIntoView({
    behavior: 'smooth'
  })} className={`bg-[#c9a227] hover:bg-[#b08d1f] text-white font-bold py-4 px-8 rounded-lg shadow-lg transform transition hover:scale-105 text-lg md:text-xl w-full md:w-auto uppercase tracking-wide ${className}`} {...props} data-id="element-9">
      {children}
    </button>;
}
function ScrollImage({
  src,
  alt,
  className
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  return <img src={src} alt={alt} className={`cursor-pointer hover:opacity-95 transition ${className}`} onClick={() => document.getElementById('comprar')?.scrollIntoView({
    behavior: 'smooth'
  })} data-id="element-10" />;
}
export function SalesPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  return <div className="font-sans text-gray-800 bg-[#f9f7f2]" data-id="element-11">
      {/* Hero Section */}
      <Section className="bg-[#1a4a4a] text-white pt-24 pb-20" data-id="element-12">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-13">
          <div className="space-y-6" data-id="element-14">
            <div className="inline-block bg-[#c9a227] text-[#1a4a4a] font-bold px-3 py-1 rounded text-sm mb-2" data-id="element-15">
              PARA DOCENTES
            </div>
            <h1 className="text-4xl md:text-6xl font-serif font-bold leading-tight text-[#c9a227]" data-id="element-16">
              💰 ECONOMÍA LISTA™
            </h1>
            <h2 className="text-xl md:text-2xl font-light text-gray-200" data-id="element-17">
              La forma simple de enseñar economía con situaciones reales que los
              estudiantes SÍ entienden
            </h2>

            <div className="space-y-3 text-lg" data-id="element-18">
              <div className="flex items-center gap-3" data-id="element-19">
                <Check className="text-[#c9a227] flex-shrink-0" data-id="element-20" />{' '}
                <span data-id="element-21">Clases listas</span>
              </div>
              <div className="flex items-center gap-3" data-id="element-22">
                <Check className="text-[#c9a227] flex-shrink-0" data-id="element-23" />{' '}
                <span data-id="element-24">Casos cotidianos</span>
              </div>
              <div className="flex items-center gap-3" data-id="element-25">
                <Check className="text-[#c9a227] flex-shrink-0" data-id="element-26" />{' '}
                <span data-id="element-27">Actividades prácticas</span>
              </div>
              <div className="flex items-center gap-3" data-id="element-28">
                <Check className="text-[#c9a227] flex-shrink-0" data-id="element-29" />{' '}
                <span data-id="element-30">Evaluaciones aplicables</span>
              </div>
            </div>

            <p className="italic text-gray-300 border-l-4 border-[#c9a227] pl-4" data-id="element-31">
              👉 Para que enseñar economía deje de ser teoría aburrida… y se
              convierta en algo que tus alumnos viven todos los días.
            </p>

            <div className="pt-4" data-id="element-32">
              <ScrollButton className="w-full md:w-auto shadow-xl shadow-black/20" data-id="element-33">
                👉 QUIERO ECONOMÍA LISTA AHORA
              </ScrollButton>
              <p className="text-xs text-center md:text-left mt-2 text-gray-400" data-id="element-34">
                Acceso inmediato – Descarga digital – Material imprimible y
                editable
              </p>
            </div>
          </div>

          <div className="relative group" data-id="element-35">
            <div className="absolute -inset-1 bg-[#c9a227] rounded-lg blur opacity-25 group-hover:opacity-50 transition duration-1000" data-id="element-36"></div>
            <ScrollImage src={IMAGES.main} alt="Economía Lista Producto Principal" className="relative rounded-lg shadow-2xl w-full transform transition hover:scale-[1.02]" data-id="element-37" />
          </div>
        </div>
      </Section>

      {/* Problem Section */}
      <Section className="bg-white" data-id="element-38">
        <div className="max-w-3xl mx-auto text-center space-y-8" data-id="element-39">
          <h2 className="text-3xl font-bold text-[#1a4a4a]" data-id="element-40">
            ❗ Profe, seamos honestos…
          </h2>
          <p className="text-xl text-gray-600" data-id="element-41">
            Enseñar economía muchas veces se siente así:
          </p>

          <div className="grid gap-4 text-left bg-red-50 p-8 rounded-xl border border-red-100" data-id="element-42">
            <div className="flex items-start gap-4" data-id="element-43">
              <span className="text-2xl" data-id="element-44">😓</span>
              <p className="text-lg" data-id="element-45">Explicás conceptos… y nadie conecta</p>
            </div>
            <div className="flex items-start gap-4" data-id="element-46">
              <span className="text-2xl" data-id="element-47">📉</span>
              <p className="text-lg" data-id="element-48">Hablás de inflación… y lo ven lejano</p>
            </div>
            <div className="flex items-start gap-4" data-id="element-49">
              <span className="text-2xl" data-id="element-50">📚</span>
              <p className="text-lg" data-id="element-51">Das teoría… pero no saben aplicarla</p>
            </div>
            <div className="flex items-start gap-4" data-id="element-52">
              <span className="text-2xl" data-id="element-53">⏳</span>
              <p className="text-lg" data-id="element-54">
                Preparar una clase interesante te toma horas
              </p>
            </div>
            <div className="flex items-start gap-4" data-id="element-55">
              <span className="text-2xl" data-id="element-56">🤷‍♂️</span>
              <p className="text-lg" data-id="element-57">Terminás improvisando ejemplos</p>
            </div>
          </div>

          <p className="text-xl font-medium text-gray-700" data-id="element-58">
            Y al final pensás: "No es que no quieran aprender… es que no lo
            están viendo en su mundo."
          </p>

          <p className="text-2xl font-bold text-[#1a4a4a]" data-id="element-59">
            El problema no es la economía. El problema es cómo se la estamos
            mostrando.
          </p>
        </div>
      </Section>

      {/* Solution Section */}
      <Section className="bg-[#f0fdfa]" data-id="element-60">
        <div className="max-w-5xl mx-auto" data-id="element-61">
          <div className="text-center mb-12" data-id="element-62">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a4a4a] mb-4" data-id="element-63">
              ✅ Con Economía Lista™ tus clases cambian desde la primera semana
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-64">
              Este no es un libro teórico más. Es un sistema de clases listo
              para usar que conecta la economía con la vida real de los
              estudiantes:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-65">
            <div className="bg-white p-6 rounded-xl shadow-md border-t-4 border-[#1a4a4a]" data-id="element-66">
              <ul className="space-y-4" data-id="element-67">
                <li className="flex items-center gap-3" data-id="element-68">
                  <Check className="text-green-600 w-6 h-6" data-id="element-69" />{' '}
                  <span className="text-lg" data-id="element-70">Inflación en el supermercado</span>
                </li>
                <li className="flex items-center gap-3" data-id="element-71">
                  <Check className="text-green-600 w-6 h-6" data-id="element-72" />{' '}
                  <span className="text-lg" data-id="element-73">Sueldos y costo de vida</span>
                </li>
                <li className="flex items-center gap-3" data-id="element-74">
                  <Check className="text-green-600 w-6 h-6" data-id="element-75" />{' '}
                  <span className="text-lg" data-id="element-76">Créditos, cuotas e intereses</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md border-t-4 border-[#c9a227]" data-id="element-77">
              <ul className="space-y-4" data-id="element-78">
                <li className="flex items-center gap-3" data-id="element-79">
                  <Check className="text-green-600 w-6 h-6" data-id="element-80" />{' '}
                  <span className="text-lg" data-id="element-81">Tarjetas, deudas y consumo</span>
                </li>
                <li className="flex items-center gap-3" data-id="element-82">
                  <Check className="text-green-600 w-6 h-6" data-id="element-83" />{' '}
                  <span className="text-lg" data-id="element-84">Economía del hogar</span>
                </li>
                <li className="flex items-center gap-3" data-id="element-85">
                  <Check className="text-green-600 w-6 h-6" data-id="element-86" />{' '}
                  <span className="text-lg" data-id="element-87">
                    Impuestos en situaciones reales
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <p className="text-center text-xl font-medium text-[#1a4a4a]" data-id="element-88">
            Todo explicado con casos cotidianos, actividades guiadas y dinámicas
            aplicables en aula.
          </p>
        </div>
      </Section>

      {/* What's Included */}
      <Section className="bg-white" data-id="element-89">
        <div className="max-w-6xl mx-auto" data-id="element-90">
          <h2 className="text-3xl font-bold text-center text-[#1a4a4a] mb-12" data-id="element-91">
            📦 ¿Qué incluye ECONOMÍA LISTA™?
          </h2>

          <div className="flex flex-col md:flex-row gap-12 items-center" data-id="element-92">
            <div className="w-full md:w-1/2" data-id="element-93">
              <ScrollImage src={IMAGES.main} alt="Ebook Principal" className="w-full rounded-lg shadow-xl" data-id="element-94" />
            </div>
            <div className="w-full md:w-1/2 space-y-6" data-id="element-95">
              <h3 className="text-2xl font-bold text-[#c9a227]" data-id="element-96">
                📘 EBOOK PRINCIPAL — Sistema Economía Lista
              </h3>
              <ul className="space-y-4" data-id="element-97">
                {['Secuencias didácticas listas', 'Casos reales explicados paso a paso', 'Actividades individuales y grupales', 'Guías de discusión', 'Evaluaciones aplicables', 'Preguntas que desarrollan pensamiento crítico'].map((item, i) => <li key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-98">
                    <BookOpen className="text-[#1a4a4a] w-5 h-5 flex-shrink-0" data-id="element-99" />
                    <span data-id="element-100">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* Bonuses */}
      <Section className="bg-[#1a4a4a] text-white" data-id="element-101">
        <div className="max-w-6xl mx-auto" data-id="element-102">
          <h2 className="text-4xl font-serif font-bold text-center text-[#c9a227] mb-4" data-id="element-103">
            🎁 BONOS INCLUIDOS
          </h2>
          <p className="text-center text-gray-300 mb-12 uppercase tracking-widest" data-id="element-104">
            Gratis por el día de hoy
          </p>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-105">
            {/* Bonus 1 */}
            <div className="bg-white/5 p-6 rounded-xl border border-white/10 hover:border-[#c9a227] transition" data-id="element-106">
              <div className="mb-4 relative" data-id="element-107">
                <span className="absolute top-2 right-2 bg-[#c9a227] text-[#1a4a4a] text-xs font-bold px-2 py-1 rounded" data-id="element-108">
                  GRATIS
                </span>
                <ScrollImage src={IMAGES.bono1} alt="Bono 1" className="w-full rounded shadow-lg" data-id="element-109" />
              </div>
              <h3 className="text-xl font-bold text-[#c9a227] mb-2" data-id="element-110">
                🎁 Bono 1 — Frases Docentes para Recuperar el Control
              </h3>
              <p className="text-gray-300" data-id="element-111">
                ✔ Bajar el ruido sin gritar ✔ Recuperar atención ✔ Redirigir
                conductas
              </p>
            </div>

            {/* Bonus 2 */}
            <div className="bg-white/5 p-6 rounded-xl border border-white/10 hover:border-[#c9a227] transition" data-id="element-112">
              <div className="mb-4 relative" data-id="element-113">
                <span className="absolute top-2 right-2 bg-[#c9a227] text-[#1a4a4a] text-xs font-bold px-2 py-1 rounded" data-id="element-114">
                  GRATIS
                </span>
                <ScrollImage src={IMAGES.bono2} alt="Bono 2" className="w-full rounded shadow-lg" data-id="element-115" />
              </div>
              <h3 className="text-xl font-bold text-[#c9a227] mb-2" data-id="element-116">
                🎁 Bono 2 — Casos Reales Explicados Fácil
              </h3>
              <p className="text-gray-300" data-id="element-117">
                ✔ Inflación cotidiana ✔ Economía del hogar ✔ Créditos y
                cuotas
              </p>
            </div>

            {/* Bonus 3 */}
            <div className="bg-white/5 p-6 rounded-xl border border-white/10 hover:border-[#c9a227] transition" data-id="element-118">
              <div className="mb-4 relative" data-id="element-119">
                <span className="absolute top-2 right-2 bg-[#c9a227] text-[#1a4a4a] text-xs font-bold px-2 py-1 rounded" data-id="element-120">
                  GRATIS
                </span>
                <ScrollImage src={IMAGES.bono3} alt="Bono 3" className="w-full rounded shadow-lg" data-id="element-121" />
              </div>
              <h3 className="text-xl font-bold text-[#c9a227] mb-2" data-id="element-122">
                🎁 Bono 3 — Kit de Participación para Cursos Difíciles
              </h3>
              <p className="text-gray-300" data-id="element-123">
                ✔ Dinámicas sin exposición ✔ Participación sin presión
              </p>
            </div>

            {/* Bonus 4 */}
            <div className="bg-white/5 p-6 rounded-xl border border-white/10 hover:border-[#c9a227] transition" data-id="element-124">
              <div className="mb-4 relative" data-id="element-125">
                <span className="absolute top-2 right-2 bg-[#c9a227] text-[#1a4a4a] text-xs font-bold px-2 py-1 rounded" data-id="element-126">
                  GRATIS
                </span>
                <ScrollImage src={IMAGES.bono4} alt="Bono 4" className="w-full rounded shadow-lg" data-id="element-127" />
              </div>
              <h3 className="text-xl font-bold text-[#c9a227] mb-2" data-id="element-128">
                🎁 Bono 4 — Plantillas de Trabajo en Grupo
              </h3>
              <p className="text-gray-300" data-id="element-129">
                ✔ Roles claros ✔ Organización de tareas
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Target Audience */}
      <Section className="bg-[#f9f7f2]" data-id="element-130">
        <div className="max-w-4xl mx-auto text-center" data-id="element-131">
          <h2 className="text-3xl font-bold text-[#1a4a4a] mb-8" data-id="element-132">
            🎯 ¿Para quién es este material?
          </h2>
          <div className="grid md:grid-cols-2 gap-4 text-left" data-id="element-133">
            {['Docentes de economía o ciencias sociales', 'Profesores con poco tiempo para planificar', 'Profes que quieren clases prácticas', 'Docentes que quieren participación real'].map((item, i) => <div key={i} className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm border border-gray-200" data-id="element-134">
                <Check className="text-[#c9a227] w-6 h-6 flex-shrink-0" data-id="element-135" />
                <span className="font-medium text-gray-700" data-id="element-136">{item}</span>
              </div>)}
          </div>
        </div>
      </Section>

      {/* Before/After */}
      <Section className="bg-white" data-id="element-137">
        <div className="max-w-4xl mx-auto" data-id="element-138">
          <h2 className="text-3xl font-bold text-center text-[#1a4a4a] mb-12" data-id="element-139">
            🔥 Qué cambia cuando usás ECONOMÍA LISTA™
          </h2>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-140">
            <div className="bg-red-50 p-8 rounded-xl border border-red-100" data-id="element-141">
              <h3 className="text-xl font-bold text-red-600 mb-4 flex items-center gap-2" data-id="element-142">
                <span data-id="element-143">❌</span> Antes
              </h3>
              <ul className="space-y-3 text-gray-700" data-id="element-144">
                <li data-id="element-145">• Teoría aburrida</li>
                <li data-id="element-146">• Desconexión total</li>
                <li data-id="element-147">• Improvisación constante</li>
                <li data-id="element-148">• Estrés al planificar</li>
              </ul>
            </div>

            <div className="bg-green-50 p-8 rounded-xl border border-green-100 shadow-lg transform md:-translate-y-4" data-id="element-149">
              <h3 className="text-xl font-bold text-green-600 mb-4 flex items-center gap-2" data-id="element-150">
                <span data-id="element-151">✅</span> Después
              </h3>
              <ul className="space-y-3 text-gray-800 font-medium" data-id="element-152">
                <li className="flex items-center gap-2" data-id="element-153">
                  <Check className="w-4 h-4" data-id="element-154" /> Participación activa
                </li>
                <li className="flex items-center gap-2" data-id="element-155">
                  <Check className="w-4 h-4" data-id="element-156" /> Claridad en los temas
                </li>
                <li className="flex items-center gap-2" data-id="element-157">
                  <Check className="w-4 h-4" data-id="element-158" /> Estructura probada
                </li>
                <li className="flex items-center gap-2" data-id="element-159">
                  <Check className="w-4 h-4" data-id="element-160" /> Ahorro de tiempo real
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* Purchase Section */}
      <Section id="comprar" className="bg-[#1a4a4a] text-white text-center py-24" data-id="element-161">
        <div className="max-w-3xl mx-auto bg-white/5 backdrop-blur-sm p-8 md:p-12 rounded-2xl border border-[#c9a227]/30 shadow-2xl" data-id="element-162">
          <h2 className="text-4xl font-serif font-bold text-[#c9a227] mb-2" data-id="element-163">
            🚀 ACCESO INMEDIATO
          </h2>
          <p className="text-gray-300 mb-8" data-id="element-164">
            Descarga todo el sistema y los bonos ahora mismo
          </p>

          <div className="flex flex-col items-center justify-center mb-8" data-id="element-165">
            <span className="text-gray-400 line-through text-xl" data-id="element-166">$47.00</span>
            <span className="text-6xl font-bold text-white" data-id="element-167">$13.99</span>
            <span className="text-[#c9a227] font-bold mt-2" data-id="element-168">
              OFERTA POR TIEMPO LIMITADO
            </span>
          </div>

          <CheckoutButton className="w-full text-xl md:text-2xl py-6 animate-pulse" data-id="element-169">
            👉 QUIERO ECONOMÍA LISTA AHORA
          </CheckoutButton>

          <div className="mt-6 flex justify-center gap-4 text-sm text-gray-400" data-id="element-170">
            <span className="flex items-center gap-1" data-id="element-171">
              <ShieldCheck className="w-4 h-4" data-id="element-172" /> Pago 100% Seguro
            </span>
            <span className="flex items-center gap-1" data-id="element-173">
              <Zap className="w-4 h-4" data-id="element-174" /> Entrega Inmediata
            </span>
          </div>
        </div>
      </Section>

      {/* Testimonials */}
      <Section className="bg-[#f9f7f2]" data-id="element-175">
        <div className="max-w-6xl mx-auto" data-id="element-176">
          <h2 className="text-3xl font-bold text-center text-[#1a4a4a] mb-12" data-id="element-177">
            Lo que dicen otros docentes
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-178">
            {TESTIMONIALS.map((t, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition" data-id="element-179">
                <div className="flex gap-1 mb-4" data-id="element-180">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#c9a227] text-[#c9a227]" data-id="element-181" />)}
                </div>
                <p className="text-gray-700 italic mb-6" data-id="element-182">"{t.text}"</p>
                <div className="flex items-center gap-3" data-id="element-183">
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-[#1a4a4a] font-bold" data-id="element-184">
                    {t.name[0]}
                  </div>
                  <div data-id="element-185">
                    <p className="font-bold text-[#1a4a4a] text-sm" data-id="element-186">{t.name}</p>
                    <p className="text-xs text-gray-500" data-id="element-187">
                      {t.role} • {t.location}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section className="bg-white" data-id="element-188">
        <div className="max-w-3xl mx-auto" data-id="element-189">
          <h2 className="text-3xl font-bold text-center text-[#1a4a4a] mb-12" data-id="element-190">
            ❓ Preguntas Frecuentes (FAQ)
          </h2>

          <div className="space-y-4" data-id="element-191">
            {FAQS.map((faq, i) => <div key={i} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-192">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition" data-id="element-193">
                  <span className="font-bold text-[#1a4a4a]" data-id="element-194">
                    {faq.question}
                  </span>
                  {openFaq === i ? <ChevronUp className="text-gray-500" data-id="element-195" /> : <ChevronDown className="text-gray-500" data-id="element-196" />}
                </button>
                {openFaq === i && <div className="p-4 bg-white text-gray-700 border-t border-gray-200" data-id="element-197">
                    {faq.answer}
                  </div>}
              </div>)}
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="bg-[#1a4a4a] text-gray-400 py-8 text-center text-sm border-t border-white/10" data-id="element-198">
        <p data-id="element-199">
          © {new Date().getFullYear()} Economía Lista™. Todos los derechos
          reservados.
        </p>
        <p className="mt-2" data-id="element-200">
          Este sitio no es parte del sitio web de Facebook o Facebook Inc.
        </p>
      </footer>
    </div>;
}